import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { projectsProjectOrderReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-order-reference';
import { z } from 'zod';
import { procurementOperationIdSchema, procurementOperationMoneyCentsSchema, procurementOperationPageSchema, procurementOperationQuantityTenThousandthsSchema, procurementOperationReasonSchema, procurementOperationStateCommandSchema, } from './procurement-operation-common.contract.js';
export const procurementReceivingCapability = 'procurement.receiving.v1';
export const procurementReceiptTypeSchema = z.enum([
    'delivery_photo', 'pickup_photo', 'packing_slip', 'shortage_photo', 'supplier_ticket', 'other',
]);
export const procurementReceiptStatusSchema = z.enum([
    'pending', 'uploaded', 'verified', 'shortage_reported', 'rejected', 'void',
]);
const procurementReceivingItemBaseSchema = z.strictObject({
    lineNumber: z.number().int().positive().nullable(),
    name: z.string().trim().min(1).max(240),
    unit: z.string().trim().min(1).max(60),
    orderedQuantityTenThousandths: procurementOperationQuantityTenThousandthsSchema,
    receivedQuantityTenThousandths: procurementOperationQuantityTenThousandthsSchema,
    shortageQuantityTenThousandths: procurementOperationQuantityTenThousandthsSchema,
    actualUnitCostCents: procurementOperationMoneyCentsSchema.nullable(),
    actualLineTotalCents: procurementOperationMoneyCentsSchema.nullable(),
    notes: z.string().trim().max(1_000).nullable(),
});
function validateReceivingItem(item, context) {
    const expectedShortage = Math.max(item.orderedQuantityTenThousandths - item.receivedQuantityTenThousandths, 0);
    if (item.shortageQuantityTenThousandths !== expectedShortage) {
        context.addIssue({ code: 'custom', path: ['shortageQuantityTenThousandths'], message: 'Shortage quantity must reconcile to ordered minus received.' });
    }
    if ((item.actualUnitCostCents === null) !== (item.actualLineTotalCents === null)) {
        context.addIssue({ code: 'custom', path: ['actualLineTotalCents'], message: 'Actual unit and line cost must be present together.' });
    }
    if (item.actualUnitCostCents !== null) {
        const expected = Number((BigInt(item.receivedQuantityTenThousandths) * BigInt(item.actualUnitCostCents) + 5000n) / 10000n);
        if (item.actualLineTotalCents !== expected) {
            context.addIssue({ code: 'custom', path: ['actualLineTotalCents'], message: 'Actual receipt line cost must reconcile exactly.' });
        }
    }
}
export const procurementReceivingItemSchema = procurementReceivingItemBaseSchema.superRefine(validateReceivingItem);
const procurementReceivingDraftBaseSchema = z.strictObject({
    projectId: z.string().uuid(),
    fulfillmentId: z.string().uuid(),
    receiptKey: z.string().trim().min(1).max(240),
    proofType: procurementReceiptTypeSchema,
    status: procurementReceiptStatusSchema,
    deliveredAt: z.string().datetime({ offset: true }).nullable(),
    receivedBy: z.strictObject({
        membershipId: z.string().uuid().nullable(),
        displayName: z.string().trim().min(1).max(240),
        company: z.string().trim().max(240).nullable(),
    }).nullable(),
    deliveryLocation: z.string().trim().max(500).nullable(),
    items: z.array(procurementReceivingItemSchema).min(1).max(250),
    objectReferences: z.array(platformStorageAttachedObjectReferenceSchema).max(50),
    notes: z.string().trim().max(8_000).nullable(),
});
function validateReceivingState(draft, context) {
    const hasShortage = draft.items.some((item) => item.shortageQuantityTenThousandths > 0);
    if (draft.status === 'shortage_reported' && !hasShortage) {
        context.addIssue({ code: 'custom', path: ['items'], message: 'A shortage receipt must contain a shortage.' });
    }
    if (draft.status === 'verified' && hasShortage) {
        context.addIssue({ code: 'custom', path: ['status'], message: 'A receipt with shortages cannot be fully verified.' });
    }
}
export const procurementReceivingDraftSchema = procurementReceivingDraftBaseSchema.superRefine(validateReceivingState);
export const procurementReceivingCreateCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    receiptId: procurementOperationIdSchema,
    receipt: procurementReceivingDraftSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementReceivingReviseCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    receipt: procurementReceivingDraftSchema,
    reason: procurementOperationReasonSchema,
});
export const procurementReceivingCommandSchema = z.discriminatedUnion('kind', [
    procurementReceivingReviseCommandSchema,
    procurementOperationStateCommandSchema,
]);
export const procurementReceivingSchema = z.strictObject({
    contractVersion: z.literal(procurementReceivingCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    costsVisible: z.boolean(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: procurementOperationReasonSchema,
    actorMembershipId: z.string().uuid(),
    data: procurementReceivingDraftBaseSchema.omit({ projectId: true }).extend({
        projectReference: projectsProjectOrderReferenceSnapshotSchema,
        items: z.array(procurementReceivingItemBaseSchema.extend({
            actualUnitCostCents: procurementOperationMoneyCentsSchema.nullable(),
            actualLineTotalCents: procurementOperationMoneyCentsSchema.nullable(),
        }).superRefine(validateReceivingItem)),
    }).superRefine(validateReceivingState),
});
export const procurementReceivingListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    projectId: z.string().uuid().nullable().default(null),
    fulfillmentId: z.string().uuid().nullable().default(null),
    status: procurementReceiptStatusSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const procurementReceivingResponseSchema = z.strictObject({
    data: procurementReceivingSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const procurementReceivingsResponseSchema = z.strictObject({
    data: z.array(procurementReceivingSchema),
    page: procurementOperationPageSchema,
});
export function calculateProcurementReceivingActualSubtotal(items) {
    if (items.some((item) => item.actualLineTotalCents === null))
        return null;
    return procurementOperationMoneyCentsSchema.parse(items.reduce((sum, item) => sum + (item.actualLineTotalCents ?? 0), 0));
}
//# sourceMappingURL=procurement-receiving.contract.js.map